import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Loader2, MoreHorizontal, Pencil, Pin, PinOff, Power, Trash2, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Urls } from '@/lib/navigation';
import { useAppSession } from '@/contexts/SessionContext';
import { useAuth } from '@/hooks/useAuth';
import { useAgentStatus } from '@/hooks/useAgentStatus';
import { useIsMobileDevice } from '@/hooks/useIsMobileDevice';
import { PaperMobileTabBar } from './PaperMobileTabBar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ModalOverlay } from '@/components/ModalOverlay';
import { sessionToAgent, formatTimeAgo, type AgentInfo, type DisplayStatus } from '@/lib/agents';
import { BackButton } from './BackButton';
import { SidebarRail, useSidebarRailActive } from './SidebarRail';
import { sessionsToRailItems } from './railItems';
import { ConditionalMobileDevToggle, ConditionalRailDevToggle } from '@/components/DevPanel';
import {
  PAPER_CHROME_RADIUS_CLASS,
  PAPER_CONTROL_RADIUS_CLASS,
  PAPER_ICON_COMPACT_CLASS,
  PAPER_ICON_CONTROL_COMPACT_CLASS,
  PAPER_ICON_MICRO_CLASS,
  PAPER_ICON_SMALL_CLASS,
  PAPER_ICON_STROKE_WIDTH,
  PAPER_ICON_TINY_CLASS,
} from './paperShapes';

type PillKind = 'running' | 'pending' | 'off';

const pillKind = (status: DisplayStatus): PillKind => {
  if (status === 'running' || status === 'on') return 'running';
  if (status === 'starting' || status === 'stopping') return 'pending';
  return 'off';
};

const PILL_LABEL: Record<PillKind, string> = {
  running: 'Running',
  pending: 'Switching',
  off: 'Off',
};

interface StatusPillProps {
  kind: PillKind;
}

const StatusPill = ({ kind }: StatusPillProps) => (
  <span
    className={cn(
      'inline-flex items-center gap-[6px] py-[3px] px-[10px] rounded-full',
      'text-[10px] tracking-[0.14em] uppercase font-medium',
      'border border-solid border-[var(--hair-subtle)]',
      kind === 'running' && 'bg-[var(--periwinkle-wash)] text-[var(--periwinkle)]',
      kind === 'pending' && 'bg-[var(--bg-alt)] text-[var(--fg-3)]',
      kind === 'off' && 'bg-[var(--bg-alt)] text-[var(--fg-4)]',
    )}
  >
    {kind === 'pending' ? (
      <Loader2
        className={cn(PAPER_ICON_MICRO_CLASS, 'animate-spin')}
        strokeWidth={PAPER_ICON_STROKE_WIDTH}
      />
    ) : (
      <span
        className={cn(
          'w-[5px] h-[5px] rounded-full',
          kind === 'running' && 'bg-[var(--periwinkle)]',
          kind === 'off' && 'bg-[var(--fg-4)]',
        )}
        style={
          kind === 'running'
            ? { boxShadow: '0 0 0 3px var(--periwinkle-wash)' }
            : undefined
        }
      />
    )}
    {PILL_LABEL[kind]}
  </span>
);

const GRID = 'grid-cols-[2fr_120px_100px_140px_40px]';

interface PinToggleButtonProps {
  pinned: boolean;
  onToggle: () => void;
}

const PinToggleButton = (props: PinToggleButtonProps) => (
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      props.onToggle();
    }}
    aria-label={props.pinned ? 'Unpin agent' : 'Pin agent'}
    title={props.pinned ? 'Unpin agent' : 'Pin agent'}
    className={cn(
      PAPER_ICON_CONTROL_COMPACT_CLASS,
      'group/pin shrink-0',
      props.pinned
        ? 'text-[var(--periwinkle)] hover:text-[var(--periwinkle)]'
        : cn(
            'text-[var(--fg-4)] hover:text-[var(--fg-2)]',
            'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100',
          ),
    )}
  >
    {props.pinned ? (
      <>
        <Pin
          className={cn(PAPER_ICON_TINY_CLASS, 'group-hover/pin:hidden')}
          strokeWidth={PAPER_ICON_STROKE_WIDTH}
        />
        <PinOff
          className={cn(PAPER_ICON_TINY_CLASS, 'hidden group-hover/pin:block')}
          strokeWidth={PAPER_ICON_STROKE_WIDTH}
        />
      </>
    ) : (
      <Pin className={PAPER_ICON_TINY_CLASS} strokeWidth={PAPER_ICON_STROKE_WIDTH} />
    )}
  </button>
);

interface AgentRowActionsProps {
  agent: AgentInfo;
  onRename: () => void;
  onStopStart: () => void;
  onDelete: () => void;
}

const AgentRowActions = (props: AgentRowActionsProps) => {
  const { agent } = props;
  const isRunning = agent.status === 'running' || agent.status === 'on';
  const isTransitioning = agent.status === 'starting' || agent.status === 'stopping';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className={cn(
            PAPER_ICON_CONTROL_COMPACT_CLASS,
          )}
        >
          <MoreHorizontal className={PAPER_ICON_COMPACT_CLASS} strokeWidth={PAPER_ICON_STROKE_WIDTH} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'min-w-[160px] rounded-lg border border-solid border-[var(--hair)]',
          'bg-[var(--surface)] p-[4px] shadow-lg',
        )}
      >
        <DropdownMenuItem
          onSelect={props.onRename}
          className={cn(
            PAPER_CONTROL_RADIUS_CLASS,
            'text-[var(--fg-2)] focus:bg-[var(--bg-alt)] focus:text-[var(--fg)] text-[12px]',
          )}
        >
          <Pencil
            className={cn(PAPER_ICON_COMPACT_CLASS, 'mr-[8px]')}
            strokeWidth={PAPER_ICON_STROKE_WIDTH}
          />
          Rename
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={props.onStopStart}
          disabled={isTransitioning}
          className={cn(
            PAPER_CONTROL_RADIUS_CLASS,
            'text-[var(--fg-2)] focus:bg-[var(--bg-alt)] focus:text-[var(--fg)] text-[12px]',
          )}
        >
          <Power
            className={cn(PAPER_ICON_COMPACT_CLASS, 'mr-[8px]')}
            strokeWidth={PAPER_ICON_STROKE_WIDTH}
          />
          {isRunning ? 'Stop' : 'Start'}
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={props.onDelete}
          className={cn(
            PAPER_CONTROL_RADIUS_CLASS,
            'text-[var(--fg-2)] focus:bg-[var(--bg-alt)] focus:text-[#F58C7A] text-[12px]',
          )}
        >
          <Trash2
            className={cn(PAPER_ICON_COMPACT_CLASS, 'mr-[8px]')}
            strokeWidth={PAPER_ICON_STROKE_WIDTH}
          />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface AgentsTableProps {
  agents: AgentInfo[];
  busyId: string | null;
  onSelect: (id: string) => void;
  onRename: (agent: AgentInfo) => void;
  onTogglePin: (agent: AgentInfo) => void;
  onStopStart: (agent: AgentInfo) => void;
  onDelete: (id: string) => void;
}

const AgentsTable = (props: AgentsTableProps) => (
  <div className="bg-[var(--surface)] border border-solid border-[var(--hair)] rounded-lg overflow-hidden">
    <div
      className={cn(
        'grid', GRID, 'items-center gap-[12px]',
        'py-[10px] px-[18px] border-b border-solid border-[var(--hair)]',
        'bg-[var(--surface-2)]',
        'text-[10px] tracking-[0.14em] uppercase text-[var(--fg-3)]',
      )}
    >
      <div>Name</div>
      <div>Status</div>
      <div>Uptime</div>
      <div>Last active</div>
      <div />
    </div>
    {props.agents.map((a, i) => {
      const kind = pillKind(a.status);
      const isLast = i === props.agents.length - 1;
      const isBusy = props.busyId === a.id;
      const handleKey = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          props.onSelect(a.id);
        }
      };
      return (
        <div
          key={a.id}
          role="button"
          tabIndex={0}
          aria-disabled={isBusy}
          onClick={() => !isBusy && props.onSelect(a.id)}
          onKeyDown={handleKey}
          className={cn(
            'group grid', GRID, 'items-center gap-[12px] w-full text-left',
            'py-[14px] px-[18px] text-[13px] cursor-pointer select-none',
            isLast ? '' : 'border-b border-solid border-[var(--hair-subtle)]',
            'transition-colors duration-150',
            isBusy
              ? 'opacity-60 cursor-wait'
              : 'hover:bg-[var(--bg-alt)] focus-visible:bg-[var(--bg-alt)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--periwinkle)] focus-visible:-outline-offset-2',
          )}
        >
          <div className="flex items-center gap-[8px] min-w-0">
            <PinToggleButton pinned={a.pinned} onToggle={() => props.onTogglePin(a)} />
            <span className="text-[var(--fg)] font-medium truncate">{a.name}</span>
          </div>
          <div>
            <StatusPill kind={kind} />
          </div>
          <div
            className={cn(
              'text-[12px]',
              kind === 'running' ? 'text-[var(--fg)]' : 'text-[var(--fg-4)]',
            )}
            style={{ fontFamily: 'var(--font-mono-paper)' }}
          >
            {a.uptime ?? '—'}
          </div>
          <div className="text-[12px] text-[var(--fg-3)]">
            {formatTimeAgo(a.lastInteractionMs)}
          </div>
          <div className="flex justify-end">
            <AgentRowActions
              agent={a}
              onRename={() => props.onRename(a)}
              onStopStart={() => props.onStopStart(a)}
              onDelete={() => props.onDelete(a.id)}
            />
          </div>
        </div>
      );
    })}
  </div>
);

const MobileAgentsList = (props: AgentsTableProps) => (
  <div className="flex flex-col gap-[10px]">
    {props.agents.map((a) => {
      const kind = pillKind(a.status);
      const isBusy = props.busyId === a.id;
      const handleKey = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          props.onSelect(a.id);
        }
      };
      return (
        <div
          key={a.id}
          role="button"
          tabIndex={0}
          aria-disabled={isBusy}
          onClick={() => !isBusy && props.onSelect(a.id)}
          onKeyDown={handleKey}
          className={cn(
            'flex items-center gap-[12px] py-[12px] px-[14px]',
            'bg-[var(--surface)] border border-solid border-[var(--hair)]',
            PAPER_CHROME_RADIUS_CLASS,
            'transition-colors duration-150 cursor-pointer select-none',
            isBusy ? 'opacity-60 cursor-wait' : 'active:bg-[var(--bg-alt)]',
          )}
        >
          {/* status dot — same visual language as desktop pill, just stripped down */}
          <span
            className={cn(
              'w-[8px] h-[8px] rounded-full shrink-0',
              kind === 'running' && 'bg-[var(--periwinkle)]',
              kind === 'pending' && 'bg-[var(--fg-4)]',
              kind === 'off' && 'bg-[var(--hair-strong)]',
            )}
            style={kind === 'running' ? { boxShadow: '0 0 0 3px var(--periwinkle-wash)' } : undefined}
          />
          <div className="flex-1 min-w-0">
            <div
              className="text-[15px] leading-[1.2] text-[var(--fg)] truncate"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              {a.name}
            </div>
            <div className="text-[11px] text-[var(--fg-4)] mt-[2px]">
              {PILL_LABEL[kind]} · last active {formatTimeAgo(a.lastInteractionMs)}
            </div>
          </div>
          <div className="shrink-0 flex items-center gap-[2px]">
            <AgentRowActions
              agent={a}
              onRename={() => props.onRename(a)}
              onStopStart={() => props.onStopStart(a)}
              onDelete={() => props.onDelete(a.id)}
            />
            <ChevronRight
              className={cn(PAPER_ICON_SMALL_CLASS, 'text-[var(--fg-4)]')}
              strokeWidth={PAPER_ICON_STROKE_WIDTH}
            />
          </div>
        </div>
      );
    })}
  </div>
);

interface RenameDialogProps {
  value: string;
  onSave: (name: string) => void;
  onClose: () => void;
}

export const RenameDialog = (props: RenameDialogProps) => {
  const [draft, setDraft] = useState(props.value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const save = () => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== props.value) props.onSave(trimmed);
    props.onClose();
  };

  return (
    <ModalOverlay className="z-[60]" onBackdropClick={props.onClose} cardClassName="mx-4">
      <h3
        className="text-[18px] text-[var(--fg)] m-0"
        style={{ fontFamily: 'var(--font-display)' }}
      >
        Rename agent
      </h3>
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') props.onClose();
        }}
        placeholder="Agent name"
        className={cn(
          'w-full h-[38px] mt-[16px] px-[14px] rounded-lg outline-none',
          'bg-[var(--input-bg)] text-[var(--fg)]',
          'border border-solid border-[var(--hair)]',
          'text-[14px] leading-none font-normal',
          'placeholder:text-[var(--fg-4)]',
          'transition-[border-color,box-shadow] duration-200',
          'focus:border-[var(--periwinkle)] focus:shadow-[0_0_0_3px_var(--periwinkle-wash)]',
        )}
        style={{ fontFamily: 'var(--font-sans-paper)' }}
      />
      <div className="mt-[20px] flex justify-end gap-[8px]">
        <button
          type="button"
          onClick={props.onClose}
          className={cn(
            'h-[34px] px-[14px] text-[12px] cursor-pointer',
            PAPER_CONTROL_RADIUS_CLASS,
            'bg-transparent text-[var(--fg-3)] border border-solid border-[var(--hair)]',
            'transition-colors duration-200 hover:text-[var(--fg)] hover:border-[var(--hair-strong)]',
          )}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={save}
          disabled={!draft.trim() || draft.trim() === props.value}
          className={cn(
            'h-[34px] px-[14px] text-[12px] font-medium cursor-pointer',
            PAPER_CONTROL_RADIUS_CLASS,
            'bg-[var(--fg)] text-[var(--fg-inverse)] border border-solid border-[var(--fg)]',
            'transition-colors duration-200 hover:bg-[var(--ink-3)] hover:border-[var(--ink-3)]',
            'disabled:opacity-40 disabled:cursor-not-allowed',
          )}
        >
          Save
        </button>
      </div>
    </ModalOverlay>
  );
};

interface DeleteDialogProps {
  onConfirm: () => void;
  onClose: () => void;
}

export const DeleteDialog = (props: DeleteDialogProps) => (
  <ModalOverlay className="z-[60]" onBackdropClick={props.onClose} cardClassName="mx-4">
    <h3
      className="text-[18px] text-[var(--fg)] m-0"
      style={{ fontFamily: 'var(--font-display)' }}
    >
      Delete agent
    </h3>
    <p className="mt-[10px] text-[13px] leading-[1.55] text-[var(--fg-3)] m-0">
      Are you sure you want to delete this agent? This will stop the runtime and remove all data.
      This action cannot be undone.
    </p>
    <div className="mt-[20px] flex justify-end gap-[8px]">
      <button
        type="button"
        onClick={props.onClose}
        className={cn(
          'h-[34px] px-[14px] text-[12px] cursor-pointer',
          PAPER_CONTROL_RADIUS_CLASS,
          'bg-transparent text-[var(--fg-3)] border border-solid border-[var(--hair)]',
          'transition-colors duration-200 hover:text-[var(--fg)] hover:border-[var(--hair-strong)]',
        )}
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={props.onConfirm}
        className={cn(
          'h-[34px] px-[14px] text-[12px] font-medium cursor-pointer',
          PAPER_CONTROL_RADIUS_CLASS,
          'bg-[#F58C7A] text-[#1a0f0c] border border-solid border-[#F58C7A]',
          'transition-colors duration-200 hover:bg-[#f2a293] hover:border-[#f2a293]',
        )}
      >
        Delete
      </button>
    </div>
  </ModalOverlay>
);

export const AgentsPaper = () => {
  const navigate = useNavigate();
  const railActive = useSidebarRailActive();
  const auth = useAuth();
  const isMobile = useIsMobileDevice();
  const { session, actions } = useAppSession();
  const { chat, chatHistory } = session;

  const account = useMemo(() => {
    if (!auth.isAuthenticated || !auth.user) return undefined;
    const u = auth.user;
    return {
      initials: `${u.firstName?.charAt(0) ?? ''}${u.lastName?.charAt(0) ?? ''}`,
      displayName: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || (u.email ?? 'Account'),
      metaLine: u.email ?? '',
      onSettings: () => navigate('/settings'),
      onLogout: () => auth.logout(),
    };
  }, [auth, navigate]);

  const { runningCount } = useAgentStatus(
    chat.activeChatId,
    chat.activeChat?.isRunning,
    chatHistory.sessions,
  );

  const railItems = useMemo(
    () => sessionsToRailItems(chatHistory.sessions, chat.activeChatId ?? null),
    [chatHistory.sessions, chat.activeChatId],
  );

  const agents = useMemo(() => {
    const all = chatHistory.sessions.map(sessionToAgent);
    const pinned = all
      .filter((a) => a.pinned)
      .sort((a, b) => (b.lastInteractionMs ?? 0) - (a.lastInteractionMs ?? 0));
    const unpinned = all.filter((a) => !a.pinned);
    return [...pinned, ...unpinned];
  }, [chatHistory.sessions]);

  const [renamingAgent, setRenamingAgent] = useState<AgentInfo | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const handleNewChat = () => navigate(Urls.home());
  const handleSelect = (id: string) => { void actions.handleSelectAgentFromIsland(id); };
  const handleTogglePin = (a: AgentInfo) => { void actions.handlePinAgent(a.id, !a.pinned); };
  const handleStopStart = (a: AgentInfo) => {
    const isRunning = a.status === 'running' || a.status === 'on';
    if (isRunning) void actions.handleStopAgent(a.id);
    else void actions.handleStartAgent(a.id);
  };
  const confirmDelete = (id: string) => {
    setConfirmDeleteId(null);
    void Promise.resolve(actions.handleDeleteAgent(id)).catch(() => {});
  };

  const listProps: AgentsTableProps = {
    agents,
    busyId: actions.switchingAgentId ?? null,
    onSelect: handleSelect,
    onRename: setRenamingAgent,
    onTogglePin: handleTogglePin,
    onStopStart: handleStopStart,
    onDelete: setConfirmDeleteId,
  };

  return (
    <div
      className="absolute inset-0 flex flex-col bg-[var(--bg)] text-[var(--fg)]"
      style={{ fontFamily: 'var(--font-sans-paper)' }}
    >
      <div className="flex-1 min-h-0 flex">
        <div className="hidden desktop:contents">
          <SidebarRail
            active={railActive}
            pinned={railItems.pinned}
            recents={railItems.recents}
            onNavigate={(id) => {
              if (id === 'chat') navigate(Urls.home());
              if (id === 'agents') navigate('/agents');
              if (id === 'settings') navigate('/settings');
            }}
            bottomSlot={(expanded) => <ConditionalRailDevToggle expanded={expanded} />}
            account={account}
          />
        </div>

        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <div
            className={cn(
              'flex items-center justify-between',
              'border-b border-solid border-[var(--hair)]',
              'px-[16px] py-[12px] desktop:px-[28px] desktop:py-[14px]',
            )}
          >
            <BackButton />
          </div>

          <div className="flex-1 overflow-auto">
            <div className="px-[16px] pt-[20px] pb-[24px] desktop:px-[40px] desktop:pt-[28px] desktop:pb-[40px] w-full max-w-[960px] mx-auto">
              <div className="flex items-end justify-between gap-[16px]">
                <div>
                  <div className="mb-[6px] text-[11px] tracking-[0.14em] uppercase font-normal text-[var(--fg-3)]">
                    Workspace
                  </div>
                  <h1
                    className="text-[28px] desktop:text-[36px] m-0 text-[var(--fg)] leading-[1.05]"
                    style={{
                      fontFamily: 'var(--font-display)',
                      letterSpacing: '-0.01em',
                      fontWeight: 400,
                    }}
                  >
                    Agents
                  </h1>
                  <div className="mt-[6px] text-[12px] text-[var(--fg-3)] flex gap-[14px]">
                    <span>
                      <b className="text-[var(--fg)] font-semibold">{runningCount}</b> running
                    </span>
                    <span className="text-[var(--hair-strong)]">·</span>
                    <span>
                      <b className="text-[var(--fg)] font-semibold">{agents.length}</b> total
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleNewChat}
                  aria-label="New agent"
                  className={cn(
                    'inline-flex items-center gap-[6px] h-[30px] px-[14px]',
                    PAPER_CONTROL_RADIUS_CLASS,
                    'text-[11px] font-medium tracking-[0.02em] cursor-pointer',
                    'bg-[var(--fg)] text-[var(--fg-inverse)] border border-solid border-[var(--fg)]',
                    'transition-colors duration-200',
                    'hover:bg-[var(--ink-3)] hover:border-[var(--ink-3)]',
                  )}
                >
                  <Plus className={PAPER_ICON_TINY_CLASS} strokeWidth={PAPER_ICON_STROKE_WIDTH} />
                  <span className="hidden desktop:inline">New agent</span>
                  <span className="desktop:hidden">New</span>
                </button>
              </div>

              <div className="mt-[24px]">
                {agents.length === 0 ? (
                  <div
                    className={cn(
                      'bg-[var(--surface)] border border-solid border-[var(--hair)] rounded-lg',
                      'py-[48px] text-center',
                    )}
                  >
                    <p className="text-[14px] text-[var(--fg-3)] m-0">No agents yet</p>
                    <p className="mt-[4px] text-[13px] text-[var(--fg-4)] m-0">
                      Start a conversation to create one
                    </p>
                  </div>
                ) : isMobile ? (
                  <MobileAgentsList {...listProps} />
                ) : (
                  <AgentsTable {...listProps} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <PaperMobileTabBar
        className="desktop:hidden"
        active="agents"
        devControlSlot={<ConditionalMobileDevToggle />}
      />

      {renamingAgent && (
        <RenameDialog
          value={renamingAgent.name}
          onSave={(newName) => { void actions.handleRenameAgent(renamingAgent.id, newName); }}
          onClose={() => setRenamingAgent(null)}
        />
      )}

      {confirmDeleteId && (
        <DeleteDialog
          onConfirm={() => confirmDelete(confirmDeleteId)}
          onClose={() => setConfirmDeleteId(null)}
        />
      )}
    </div>
  );
};
